import DiscontinueModal, {
  DeactivationMethod,
} from 'components/global/DiscontinueModal/DiscontinueModal';
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { formatDate } from '../../../../../util/util';
import { formatISO } from 'date-fns';
import { useToast } from 'util/hooks';
import {
  useDeactivateSubscriptionMutation,
  useDiscontinueSubscriptionMutation,
} from 'redux/services/api.generated';
import { useTranslation } from 'react-i18next';

interface EndSubscriptionModalProps {
  readonly isOpen: boolean;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly subscriptionId: string;
  readonly invoicePeriodEndDate?: string | null;
  readonly refetchSubscriptions: () => void;
}

function EndSubscriptionModal({
  isOpen,
  setIsOpen,
  subscriptionId,
  invoicePeriodEndDate,
  refetchSubscriptions,
}: EndSubscriptionModalProps) {
  const toast = useToast();
  const { t } = useTranslation();

  const [discontinue] = useDiscontinueSubscriptionMutation();
  const [deactivate] = useDeactivateSubscriptionMutation();

  const handleSubmit = (
    event: FormEvent<HTMLFormElement>,
    inactiveDate: Date | null,
    closeModal: () => void,
    deactivationMethod: DeactivationMethod,
  ) => {
    event.preventDefault();
    closeModal();

    if (deactivationMethod === DeactivationMethod.discontinue) {
      discontinue({ subscriptionId })
        .unwrap()
        .then(() => {
          refetchSubscriptions();
          toast.success(
            t('subscriptions.subscriptionHasBeenDiscontinued', {
              subscriptionId,
              date: formatDate(invoicePeriodEndDate ?? ''),
            }),
          );
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
    if (deactivationMethod === DeactivationMethod.deactivateToday) {
      deactivate({ subscriptionId })
        .unwrap()
        .then(() => {
          refetchSubscriptions();
          toast.success(
            t('subscriptions.subscriptionHasBeenDeactivated', {
              subscriptionId,
            }),
          );
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
    if (deactivationMethod === DeactivationMethod.deactivateOnDate) {
      deactivate({
        subscriptionId,
        inactiveDate: inactiveDate ? formatDate(formatISO(inactiveDate)) : undefined,
      })
        .unwrap()
        .then(() => {
          refetchSubscriptions();
          toast.success(
            t('subscriptions.subscriptionWillBeDeactivated', {
              subscriptionId,
              date: formatDate(formatISO(inactiveDate as Date)),
            }),
          );
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
  };

  return (
    <DiscontinueModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={handleSubmit}
      invoicePeriodEndDate={invoicePeriodEndDate}
      title={`${t('subscriptions.endSubscription')} ${subscriptionId}`}
      description={t('subscriptions.whenDeactivateSubscription')}
      datePickerMaxDate={invoicePeriodEndDate ?? undefined}
    />
  );
}

export default EndSubscriptionModal;
